import { useState } from 'react';
import type {
  PutRoutingTimeWindowRequest,
  RoutingSkuTargetRecord,
  RoutingTimeWindowRecord,
  RoutingTimeWindowTargetOverride,
} from '@nadoutong/contracts';
import {
  useDeleteRoutingConfigTimeWindow,
  useSetRoutingConfigTimeWindow,
} from '../../api/useTenantRouting.ts';
import { coerceErrorMessage, useToast } from '../../components/Toast.tsx';
import { useT } from '../../i18n/index.ts';
import { Button, Checkbox, Dialog, FormField, Input, Switch } from '../../ui/index.ts';
import { formatMinuteOfDay } from './routingDayTimeline.ts';

/**
 * Create or edit one routing time window (0044).
 *
 * The form has exactly two overlayable columns per leg — weight and retry
 * order — and that is not a simplification of a richer server contract: the
 * contract has nothing else. There is deliberately no control here for
 * enabling, disabling, adding or repointing a leg, because a schedule that
 * could do any of those could configure a SKU into having no route at 03:00.
 *
 * The whole override set is replaced on save (the group is the unit), so a leg
 * left blank in both columns is a leg the window stops touching.
 */

interface Props {
  readonly configId: string;
  /** `null` = create a new window for this config. */
  readonly window: RoutingTimeWindowRecord | null;
  /** The selected SKU's pool — the legs this form can re-weight. */
  readonly targets: readonly RoutingSkuTargetRecord[];
  readonly onClose: () => void;
}

interface LegDraft {
  readonly weight: string;
  readonly retryOrder: string;
}

const WEEKDAY_KEYS = [
  'tenant_routing.time_windows.day_mon',
  'tenant_routing.time_windows.day_tue',
  'tenant_routing.time_windows.day_wed',
  'tenant_routing.time_windows.day_thu',
  'tenant_routing.time_windows.day_fri',
  'tenant_routing.time_windows.day_sat',
  'tenant_routing.time_windows.day_sun',
] as const;

/** `22:00` → 1320. Returns null for anything that is not a wall clock. */
export function parseClock(value: string): number | null {
  const match = /^([0-9]{1,2}):([0-9]{2})$/.exec(value.trim());
  if (match === null) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 24 || minutes > 59) return null;
  const total = hours * 60 + minutes;
  return total > 1440 ? null : total;
}

function initialLegs(
  targets: readonly RoutingSkuTargetRecord[],
  window: RoutingTimeWindowRecord | null,
): Record<string, LegDraft> {
  const overrides = new Map(
    (window?.overrides ?? []).map((override) => [override.target_id, override]),
  );
  const drafts: Record<string, LegDraft> = {};
  for (const target of targets) {
    const override = overrides.get(target.id);
    drafts[target.id] = {
      weight: override?.weight === undefined || override.weight === null
        ? ''
        : String(override.weight),
      retryOrder: override?.retry_order === undefined || override.retry_order === null
        ? ''
        : String(override.retry_order),
    };
  }
  return drafts;
}

export function TenantRoutingTimeWindowDialog({
  configId,
  window,
  targets,
  onClose,
}: Props): JSX.Element {
  const t = useT();
  const toast = useToast();
  const save = useSetRoutingConfigTimeWindow();
  const remove = useDeleteRoutingConfigTimeWindow();

  const [label, setLabel] = useState(window?.label ?? '');
  const [timezone, setTimezone] = useState(
    window?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
  );
  const [start, setStart] = useState(formatMinuteOfDay(window?.start_minute ?? 1320));
  const [end, setEnd] = useState(formatMinuteOfDay(window?.end_minute ?? 480));
  const [days, setDays] = useState<number>(window?.days_of_week ?? 127);
  const [enabled, setEnabled] = useState(window?.enabled ?? true);
  const [legs, setLegs] = useState<Record<string, LegDraft>>(
    () => initialLegs(targets, window),
  );

  const startMinute = parseClock(start);
  const endMinute = parseClock(end);
  const overrides: readonly RoutingTimeWindowTargetOverride[] = targets
    .map((target) => {
      const draft = legs[target.id] ?? { weight: '', retryOrder: '' };
      const weight = draft.weight.trim() === '' ? null : Number(draft.weight);
      const retryOrder = draft.retryOrder.trim() === '' ? null : Number(draft.retryOrder);
      return { target_id: target.id, weight, retry_order: retryOrder };
    })
    // A leg with neither column filled is a leg this window does not touch;
    // the server refuses a stored no-op override, so it is dropped here too.
    .filter((override) => override.weight !== null || override.retry_order !== null);

  const numbersValid = overrides.every((override) =>
    (override.weight === null
      || (Number.isSafeInteger(override.weight) && override.weight >= 0))
    && (override.retry_order === null
      || (Number.isSafeInteger(override.retry_order) && override.retry_order >= 1)));

  const valid = label.trim().length > 0
    && timezone.trim().length > 0
    && startMinute !== null && startMinute <= 1439
    && endMinute !== null && endMinute >= 1
    && startMinute !== endMinute
    && days >= 1 && days <= 127
    && overrides.length > 0
    && numbersValid;

  async function handleSave(): Promise<void> {
    if (!valid || startMinute === null || endMinute === null) return;
    const body: PutRoutingTimeWindowRequest = {
      label: label.trim(),
      timezone: timezone.trim(),
      days_of_week: days,
      start_minute: startMinute,
      end_minute: endMinute,
      enabled,
      overrides,
    };
    try {
      await save.mutateAsync({ configId, windowId: window?.id ?? null, body });
      toast.success(t('tenant_routing.time_windows.saved'));
      onClose();
    } catch (error) {
      // Overlap, unknown zone and foreign-target refusals all arrive as the
      // gateway's own message; surfacing it verbatim is the point — a
      // generic "save failed" would hide which two windows collided.
      toast.error(coerceErrorMessage(error));
    }
  }

  async function handleDelete(): Promise<void> {
    if (window === null) return;
    try {
      await remove.mutateAsync({ configId, windowId: window.id });
      toast.success(t('tenant_routing.time_windows.deleted'));
      onClose();
    } catch (error) {
      toast.error(coerceErrorMessage(error));
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => { if (!open) onClose(); }}
      title={t(window === null
        ? 'tenant_routing.time_windows.dialog_title_new'
        : 'tenant_routing.time_windows.dialog_title_edit')}
      description={t('tenant_routing.time_windows.dialog_description')}
      closeLabel={t('common.close')}
      size="lg"
      testId="tenant-routing-time-window-dialog"
      footer={<>
        {window === null ? null : (
          <Button
            disabled={remove.isPending}
            onClick={() => { void handleDelete(); }}
            data-testid="tenant-routing-time-window-delete"
          >
            {t('tenant_routing.time_windows.delete')}
          </Button>
        )}
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="primary"
          disabled={!valid || save.isPending}
          onClick={() => { void handleSave(); }}
          data-testid="tenant-routing-time-window-save"
        >
          {t('tenant_routing.time_windows.save')}
        </Button>
      </>}
    >
      <div className="flex flex-col gap-4">
        <FormField label={t('tenant_routing.time_windows.label')} fieldId="tenant-routing-window-label">
          <Input
            id="tenant-routing-window-label"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            data-testid="tenant-routing-time-window-label"
          />
        </FormField>

        <div className="grid grid-cols-3 gap-3">
          <FormField label={t('tenant_routing.time_windows.start')} fieldId="tenant-routing-window-start">
            <Input
              id="tenant-routing-window-start"
              value={start}
              onChange={(event) => setStart(event.target.value)}
              placeholder="22:00"
              data-testid="tenant-routing-time-window-start"
            />
          </FormField>
          <FormField label={t('tenant_routing.time_windows.end')} fieldId="tenant-routing-window-end">
            <Input
              id="tenant-routing-window-end"
              value={end}
              onChange={(event) => setEnd(event.target.value)}
              placeholder="08:00"
              data-testid="tenant-routing-time-window-end"
            />
          </FormField>
          <FormField label={t('tenant_routing.time_windows.timezone')} fieldId="tenant-routing-window-tz">
            <Input
              id="tenant-routing-window-tz"
              value={timezone}
              onChange={(event) => setTimezone(event.target.value)}
              placeholder="Asia/Shanghai"
              data-testid="tenant-routing-time-window-timezone"
            />
          </FormField>
        </div>
        {/* Stated rather than inferred: 22:00→08:00 is a normal configuration
            and the form must not read as an invalid range. */}
        <p className="m-0 text-dense text-paper-low">
          {startMinute !== null && endMinute !== null && startMinute >= endMinute
            ? t('tenant_routing.time_windows.wrap_note')
            : t('tenant_routing.time_windows.range_note')}
        </p>

        <div className="flex flex-wrap items-center gap-3">
          {WEEKDAY_KEYS.map((key, index) => (
            <Checkbox
              key={key}
              checked={((days >> index) & 1) === 1}
              onCheckedChange={(checked) =>
                setDays((current) => (checked
                  ? current | (1 << index)
                  : current & ~(1 << index)))}
              label={t(key)}
              testId={`tenant-routing-time-window-day-${String(index)}`}
            />
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Switch
            checked={enabled}
            onCheckedChange={setEnabled}
            label={t('tenant_routing.time_windows.enabled')}
            testId="tenant-routing-time-window-enabled"
          />
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-label text-paper-mid">{t('tenant_routing.time_windows.legs')}</span>
          <p className="m-0 text-dense text-paper-low">{t('tenant_routing.time_windows.legs_hint')}</p>
          {targets.filter((target) => target.enabled).map((target) => (
            <div key={target.id} className="flex flex-wrap items-center gap-2">
              <span className="min-w-40 font-mono text-dense text-paper">
                {target.upstream_channel_slug}
              </span>
              <span className="font-mono text-micro text-paper-low">
                {t('tenant_routing.time_windows.base', {
                  weight: String(target.weight),
                  retry: target.retry_order === null
                    ? t('tenant_routing.pool.retry_order_auto')
                    : String(target.retry_order),
                })}
              </span>
              <Input
                wrapperClassName="w-24"
                value={legs[target.id]?.weight ?? ''}
                onChange={(event) => setLegs((current) => ({
                  ...current,
                  [target.id]: {
                    weight: event.target.value,
                    retryOrder: current[target.id]?.retryOrder ?? '',
                  },
                }))}
                placeholder={t('tenant_routing.default_channels.weight')}
                aria-label={`${target.upstream_channel_slug} ${t('tenant_routing.default_channels.weight')}`}
                data-testid={`tenant-routing-time-window-weight-${target.id}`}
              />
              <Input
                wrapperClassName="w-24"
                value={legs[target.id]?.retryOrder ?? ''}
                onChange={(event) => setLegs((current) => ({
                  ...current,
                  [target.id]: {
                    weight: current[target.id]?.weight ?? '',
                    retryOrder: event.target.value,
                  },
                }))}
                placeholder={t('tenant_routing.pool.retry_order')}
                aria-label={`${target.upstream_channel_slug} ${t('tenant_routing.pool.retry_order')}`}
                data-testid={`tenant-routing-time-window-retry-${target.id}`}
              />
            </div>
          ))}
        </div>
      </div>
    </Dialog>
  );
}
